// Queries and writes the screens share: me, the trips, one trip's model, and the map catalog.
import type { MeResponse, RowSnapshot } from '@pf/api-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api } from '../api.ts';
import { tripModel } from '../model/trip.ts';
import { type Edit, observe, write } from '../sync.ts';

export type Me = z.infer<typeof MeResponse>;

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });

export function useTrips() {
  return useQuery({
    queryKey: ['trips'],
    queryFn: async () => {
      const { rows } = await api<{ rows: RowSnapshot[] }>('/trips');
      observe(rows);
      return rows.filter((r) => r.values.deletedAt == null);
    },
  });
}

/** Live trips with their destinations, for cards and codes. */
export function useTripCards() {
  const { data, isPending, error } = useTrips();
  const rows = data ?? [];
  const trips = rows.filter((r) => r.table === 'trip');
  const places = rows.filter((r) => r.table === 'destination');
  const codes = tripCodes(
    trips.map((t) => ({
      id: t.id,
      start: (t.values.startDate as string | null) ?? null,
      country: (places.find((d) => d.tripId === t.id)?.values.countryCode as string | null) ?? null,
    })),
  );
  return {
    data,
    isPending,
    error,
    cards: trips.map((t) => ({
      id: t.id,
      title: String(t.values.title || 'Untitled trip'),
      start: (t.values.startDate as string | null) ?? null,
      end: (t.values.endDate as string | null) ?? null,
      archived: t.values.archivedAt != null,
      code: codes[t.id] ?? 'TR--',
      places: places.filter((d) => d.tripId === t.id).map((d) => String(d.values.name ?? '')),
    })),
  };
}

/** `JP26-A`: first destination's country + 2-digit year + a letter in start order (as PFStore TripCode). */
export function tripCodes(
  trips: { id: string; start: string | null; country: string | null }[],
): Record<string, string> {
  const prefix = (t: { start: string | null; country: string | null }) => {
    const cc = (t.country ?? '')
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, 2);
    return `${cc.length === 2 ? cc : 'TR'}${t.start ? t.start.slice(2, 4) : '--'}`;
  };
  const out: Record<string, string> = {};
  const groups = new Map<string, typeof trips>();
  for (const t of trips) groups.set(prefix(t), [...(groups.get(prefix(t)) ?? []), t]);
  for (const [p, list] of groups) {
    list
      .sort((a, b) => (a.start ?? '9999').localeCompare(b.start ?? '9999') || a.id.localeCompare(b.id))
      .forEach((t, i) => {
        out[t.id] = `${p}-${i < 26 ? String.fromCharCode(65 + i) : i + 1}`;
      });
  }
  return out;
}

/** Module-level, so react-query can keep the model between renders (an inline `select` reruns every time). */
const selectModel = (rows: RowSnapshot[]) => tripModel(rows);

/** Tables the trips list shows. */
const LISTED = new Set(['trip', 'destination']);

export function useTrip(tripId: string) {
  const queries = useQueryClient();
  const key = ['bundle', tripId];
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const b = await api<{ rows: RowSnapshot[] }>(`/trips/${tripId}/bundle`);
      observe(b.rows);
      return b.rows;
    },
    select: selectModel,
  });
  /**
   * Push edits; resolves with how many were refused. They show at once and stay: the server merges
   * them as `applyEdits` does, so nothing is fetched back. A refused edit is replaced by the
   * server's row; a failed push refetches what's really there.
   */
  const save = async (edits: Edit[]) => {
    await queries.cancelQueries({ queryKey: key });
    queries.setQueryData<RowSnapshot[]>(key, (rows) => (rows ? applyEdits(rows, edits) : rows));
    const listed = edits.some((e) => LISTED.has(e.table));
    try {
      const res = await write(edits);
      queries.setQueryData<RowSnapshot[]>(key, (rows) => (rows ? settle(rows, edits, res) : rows));
      if (listed) void queries.invalidateQueries({ queryKey: ['trips'] });
      return res.rejected.length;
    } catch (err) {
      void queries.invalidateQueries({ queryKey: key });
      if (listed) void queries.invalidateQueries({ queryKey: ['trips'] });
      throw err;
    }
  };
  return { model: query.data ?? null, loading: query.isPending, error: query.error, save };
}

/** Edits merged into the cached rows, as the server will (field by field; new rows appended). */
function applyEdits(rows: RowSnapshot[], edits: Edit[], hlc?: string): RowSnapshot[] {
  const out = [...rows];
  for (const e of edits) {
    const i = out.findIndex((r) => r.table === e.table && r.id === e.id);
    const row = out[i];
    const clocks = hlc ? Object.fromEntries(Object.keys(e.fields).map((f) => [f, hlc])) : {};
    if (row) out[i] = { ...row, values: { ...row.values, ...e.fields }, hlc: { ...row.hlc, ...clocks } };
    else
      out.push({
        table: e.table,
        id: e.id,
        tripId: e.tripId,
        ownerUserId: e.ownerUserId ?? null,
        values: e.fields,
        hlc: clocks,
      });
  }
  return out;
}

/** After the push: accepted edits get their clock; refused ones take the server's row (or go). */
function settle(rows: RowSnapshot[], edits: Edit[], res: Awaited<ReturnType<typeof write>>): RowSnapshot[] {
  const refused = new Set(res.rejected.map((r) => `${r.table}/${r.id}`));
  const accepted = edits.filter((e) => !refused.has(`${e.table}/${e.id}`));
  const out = applyEdits(rows, accepted, res.hlc).filter((r) => !refused.has(`${r.table}/${r.id}`));
  return [...out, ...res.rows.filter((r) => r.values.deletedAt == null)];
}

export interface MapArchive {
  name: string;
  bytes: number;
  bounds: [number, number, number, number];
}

export const useMapCatalog = () =>
  useQuery({
    queryKey: ['maps'],
    queryFn: async () => (await api<{ maps: MapArchive[] }>('/maps')).maps,
    staleTime: 3_600_000,
  });

/** The archive covering most points, then the smallest. */
export function regionFor(catalog: MapArchive[] | undefined, points: [number, number][]): MapArchive | null {
  const area = (a: MapArchive) => (a.bounds[2] - a.bounds[0]) * (a.bounds[3] - a.bounds[1]);
  let best: { a: MapArchive; n: number } | null = null;
  for (const a of catalog ?? []) {
    const n = points.filter(
      ([lat, lng]) => lng >= a.bounds[0] && lat >= a.bounds[1] && lng <= a.bounds[2] && lat <= a.bounds[3],
    ).length;
    if (n > 0 && (!best || n > best.n || (n === best.n && area(a) < area(best.a)))) best = { a, n };
  }
  return best?.a ?? null;
}
